import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react'
import { ColorValue, ScrollViewProps, StyleProp, View, ViewStyle, Animated } from 'react-native'
// ScrollView needs to be imported from react-native-gesture-handler
// to prevent gesture conflicts in BottomSheet component
import { ScrollView } from 'react-native-gesture-handler'
import { StyleService, useStyleSheet } from '@src/style/service'
import Device from '@config/device'
import { ContentFade, ContentFadeEdge } from './ContentFade'

export interface ScrollViewWithFadeProps {
  children?: React.ReactNode
  style?: StyleProp<ViewStyle>
  scrollViewStyle?: StyleProp<ViewStyle>
  fadeColor?: ColorValue
  onLayout?: ScrollViewProps['onLayout']
  onScrollViewLayout?: ScrollViewProps['onLayout']
  onContentSizeChange?: ScrollViewProps['onContentSizeChange']
  onScroll?: ScrollViewProps['onScroll']
}

export const ScrollViewWithFade = forwardRef<ScrollView, ScrollViewWithFadeProps>((props, ref) => {
  const {
    children,
    style,
    scrollViewStyle,
    fadeColor,
    onLayout = () => 0,
    onScrollViewLayout = () => 0,
    onContentSizeChange = () => 0,
    onScroll,
  } = props
  const styles = useStyleSheet(themedStyles)
  const scrollViewRef = useRef<ScrollView | null>(null)
  const [itemContainerHeight, setItemContainerHeight] = useState(0)
  const [scrollContentHeight, setScrollContentHeight] = useState(0)
  const [isTop, setIsTop] = useState(true)
  const [isBottom, setIsBottom] = useState(false)
  const isScrollable = itemContainerHeight < scrollContentHeight

  // Animated values for fade transitions
  const topFadeOpacity = useRef(new Animated.Value(0)).current
  const bottomFadeOpacity = useRef(new Animated.Value(0)).current

  // Animate fade visibility changes
  useEffect(() => {
    const animationConfig = {
      toValue: !isTop ? 1 : 0,
      duration: 200,
      useNativeDriver: true,
    }

    Animated.timing(topFadeOpacity, animationConfig).start()
  }, [isTop, topFadeOpacity])

  useEffect(() => {
    const animationConfig = {
      toValue: !isBottom ? 1 : 0,
      duration: 200,
      useNativeDriver: true,
    }

    Animated.timing(bottomFadeOpacity, animationConfig).start()
  }, [isBottom, bottomFadeOpacity])

  useImperativeHandle(ref, () => scrollViewRef.current as ScrollView)

  return (
    <View
      style={[styles.container, style]}
      onLayout={(e) => {
        setItemContainerHeight(e.nativeEvent.layout.height)
        onLayout(e)
      }}
    >
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={[styles.scrollView, scrollViewStyle]}
        showsVerticalScrollIndicator={false}
        onContentSizeChange={(contentWidth, contentHeight) => {
          setScrollContentHeight(contentHeight)
          onContentSizeChange(contentWidth, contentHeight)
        }}
        scrollEventThrottle={16}
        onScroll={(event) => {
          const { contentOffset, layoutMeasurement } = event.nativeEvent
          const isAtTop = contentOffset.y <= 0
          // 0.01 due to float values calculation
          const isAtBottom =
            contentOffset.y + layoutMeasurement.height >= scrollContentHeight - 0.01

          setIsTop(isAtTop)
          setIsBottom(isAtBottom)
          onScroll?.(event)
        }}
        onLayout={onScrollViewLayout}
        scrollEnabled={isScrollable}
      >
        {children}
      </ScrollView>
      {isScrollable && (
        <View style={styles.itemFadeContainer} pointerEvents="none">
          <Animated.View style={[styles.itemFadeIn, { opacity: topFadeOpacity }]}>
            <ContentFade
              edge={ContentFadeEdge.Top}
              style={styles.itemFadeContent}
              fadeColor={fadeColor}
            />
          </Animated.View>
          <Animated.View style={[styles.itemFadeOut, { opacity: bottomFadeOpacity }]}>
            <ContentFade
              edge={ContentFadeEdge.Bottom}
              style={styles.itemFadeContent}
              fadeColor={fadeColor}
            />
          </Animated.View>
        </View>
      )}
    </View>
  )
})

const themedStyles = StyleService.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flexGrow: 1,
  },
  itemFadeContainer: {
    position: 'absolute',
    left: 0,
    width: '100%',
    height: '100%',
  },
  itemFadeIn: {
    position: 'absolute',
    left: 0,
    width: '100%',
    height: 24,
  },
  itemFadeContent: {
    width: '100%',
    height: '100%',
  },
  itemFadeOut: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: 24,
    ...(Device.web ? { transform: 'rotate(180deg)' } : {}),
  },
})
